import { useEffect, useState, useRef } from 'preact/hooks'
import { SEARCH_CONFIG } from 'libs/searchspring/components/quick-search/QuickSearchDropdown'

/**
 * @typedef {Object} SearchResultState
 * @property {string} title - The title text for the search results
 * @property {boolean} hasResults - Whether there are results from the search
 * @property {boolean} showNoResults - Whether to show the no results message
 * @property {boolean} isCheckingResults - Whether the results are being checked
 */

/**
 * This hook is used to manage the state of the search results.
 * It determines the title, whether there are results, whether to show the no results message, and whether the results are being checked.
 * It also handles the timeout for the no results message and loading state to prevent flickering.
 * @param {Object} props
 * @param {string} props.query - The search query
 * @param {Object} props.results - The search results
 * @param {boolean} props.isLoading - Whether the search is loading
 * @returns {SearchResultState} The search result state
 */
export function useSearchResultState({ query, results, isLoading }) {
  const [title, setTitle] = useState(SEARCH_CONFIG.NO_RESULTS.mainTitle)
  const [hasResults, setHasResults] = useState(false)
  const [showNoResults, setShowNoResults] = useState(false)
  const [isCheckingResults, setIsCheckingResults] = useState(false)
  const noResultsTimeoutRef = useRef(null)
  const [isRecommendationsReady, setIsRecommendationsReady] = useState(false)
  const [isResultsReady, setIsResultsReady] = useState(false)
  const recommendationsTimeoutRef = useRef(null)
  const resultsTimeoutRef = useRef(null)
  // Update title and results state based on query and results
  useEffect(() => {
    const hasEmptyQuery = !query || query === 'null' || query === 'undefined'
    const hasRedirect = results?.merchandising?.redirect
    const hasNoResults = (!results?.results?.length || !results?.pagination?.totalResults) && !hasRedirect
    const hasProducts = results?.results?.length > 0

    // Clear any existing timeout
    if (noResultsTimeoutRef.current) {
      clearTimeout(noResultsTimeoutRef.current)
      noResultsTimeoutRef.current = null
    }

    setTitle(() => {
      if (hasEmptyQuery) return SEARCH_CONFIG.NO_SEARCH.mainTitle

      if (hasNoResults && !isLoading) {
        return SEARCH_CONFIG.NO_RESULTS.mainTitle
      }

      if (hasRedirect && !hasProducts) return SEARCH_CONFIG.REDIRECT.mainTitle

      return hasNoResults ? SEARCH_CONFIG.NO_SEARCH.mainTitle : `${SEARCH_CONFIG.HAS_RESULTS.mainTitle} (${results?.pagination?.totalResults})`
    })
    // Show loading state immediately for no results
    if (hasNoResults && !hasEmptyQuery && !isLoading) {
      setIsCheckingResults(true)
      noResultsTimeoutRef.current = setTimeout(() => {
        setShowNoResults(true)
        setIsCheckingResults(false)
      }, 800)
    } else {
      setIsCheckingResults(false)
      setShowNoResults(false)
    }

    setHasResults(!hasNoResults && !hasEmptyQuery)

    // Cleanup timeout on unmount
    return () => {
      if (noResultsTimeoutRef.current) {
        clearTimeout(noResultsTimeoutRef.current)
      }
    }
  }, [results, query, isLoading])

  useEffect(() => {
    if (!hasResults && query && !isLoading && showNoResults) {
      setIsRecommendationsReady(false)
      recommendationsTimeoutRef.current = setTimeout(() => {
        setIsRecommendationsReady(true)
      }, 100)
    } else {
      setIsRecommendationsReady(false)
    }

    return () => {
      if (recommendationsTimeoutRef.current) {
        clearTimeout(recommendationsTimeoutRef.current)
      }
    }
  }, [hasResults, query, isLoading, showNoResults])

  useEffect(() => {
    if (query && !isLoading) {
      setIsResultsReady(false)
      resultsTimeoutRef.current = setTimeout(() => {
        setIsResultsReady(true)
      }, 100)
    } else {
      setIsResultsReady(false)
    }

    return () => {
      if (resultsTimeoutRef.current) {
        clearTimeout(resultsTimeoutRef.current)
      }
    }
  }, [query, isLoading])

  return { title, hasResults, showNoResults, isCheckingResults, isRecommendationsReady, isResultsReady }
}
